import { select } from 'd3-selection';
import { line, curveLinearClosed } from 'd3-shape';
import { scalarToColor } from '../utils/colors';
import { scaleLinear } from 'd3-scale';
import { DataProvider } from '../data-provider';

var TernaryPlot =
/** @class */
function () {
  function TernaryPlot(svg, dp, name, upsideDown) {
    if (upsideDown === void 0) {
      upsideDown = false;
    }

    var _this = this;

    this.colorMapRange = [0, 1];
    this.selectedSamples = new Set();
    this.mouseDown = false;
    this.name = name;
    this.svg = svg;
    this.dp = dp;
    this.upsideDown = upsideDown;
    this.vertices = [[0, 0], [0, 0], [0, 0]];
    this.rootGroup = select(svg).append('g').classed("" + name, true);
    this.dataGroup = this.rootGroup.append('g').classed('data', true);
    this.gridGroup = this.rootGroup.append('g').classed('grid', true).style('pointer-events', 'none');
    this.dataTooltip = select(this.svg.parentElement).append("div").attr("class", "tooltip").style("opacity", 0).style('position', 'absolute').style('text-align', 'center').style('background-color', 'lightsteelblue').style('border-radius', '0.5rem').style('pointer-events', 'none').style('padding', '0.5rem').style('font-family', 'sans-serif').style('font-size', 'small');
    this.svg.addEventListener('mousedown', function () {
      _this.mouseDown = true;

      var mouseUpListener = function mouseUpListener() {
        window.removeEventListener('mouseup', mouseUpListener);
        _this.mouseDown = false;
      };

      window.addEventListener('mouseup', mouseUpListener);
    }); // dragging randomly gets in the way

    this.svg.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });
  }

  TernaryPlot.prototype.setSize = function (left, right) {
    // In an svg there can be several ternary plots,
    // set the left/right boundaries of this plot
    var h = this.svg.parentElement.clientHeight; // Base and height of triangle

    var base = right - left;
    var height = base * Math.sin(2 * Math.PI / 6);
    this.edge = base;

    if (this.upsideDown) {
      this.vertices[0] = [left, (h - height) / 2];
      this.vertices[1] = [right, (h - height) / 2];
      this.vertices[2] = [left + base / 2, h - (h - height) / 2];
    } else {
      this.vertices[0] = [left, h - (h - height) / 2];
      this.vertices[1] = [right, h - (h - height) / 2];
      this.vertices[2] = [left + base / 2, (h - height) / 2];
    }

    this.render();
  };

  TernaryPlot.prototype.render = function () {
    this.drawGrid();
    this.drawData();
  };

  TernaryPlot.prototype.drawGrid = function () {
    var _this = this;

    var circles = this.gridGroup.selectAll('circle').data(this.vertices); // Update

    circles.attr('cx', function (d) {
      return d[0];
    }).attr('cy', function (d) {
      return d[1];
    }).attr('r', 3); // Enter

    circles.enter().append('circle').attr('cx', function (d) {
      return d[0];
    }).attr('cy', function (d) {
      return d[1];
    }).attr('r', 3);
    var axes = [this.dp.getAxis(0), this.dp.getAxis(1), this.dp.getAxis(2)];

    if (!axes[0]) {
      return;
    }

    var labels = this.gridGroup.selectAll('text').data(this.vertices);
    labels.enter().append('text').attr('x', function (d) {
      return d[0];
    }).attr('y', function (d, i) {
      if (_this.upsideDown && i !== 2 || !_this.upsideDown && i === 2) {
        return d[1] - 24;
      } else {
        return d[1] + 48;
      }
    }).attr('text-anchor', 'middle').attr('font-family', 'sans-serif').attr('font-size', '1.5rem').text(function (d, i) {
      d;
      return _this.dp.getAxisLabel(i);
    });
    this.gridGroup.selectAll('path').remove();
    var outlineFn = line().x(function (d) {
      return d[0];
    }).y(function (d) {
      return d[1];
    }).curve(curveLinearClosed);
    this.gridGroup.append('path').datum(this.vertices).attr('d', outlineFn).attr('fill', 'none').attr('stroke', 'rgba(0, 0, 0, 0.7').attr('stroke-width', 1.5);
    var gridlineFn = line().x(function (d) {
      return d[0];
    }).y(function (d) {
      return d[1];
    }); // Add major grid lines

    for (var i = 0; i < 3; ++i) {
      var delta = axes[i].range[1] - axes[i].range[0];
      var spacing = axes[i].spacing;
      var n = Math.round(delta / spacing);

      for (var j = 1; j < n; ++j) {
        var points = [[axes[0].range[0], axes[1].range[0], axes[2].range[0]], [axes[0].range[0], axes[1].range[0], axes[2].range[0]]];
        var frac = j * axes[i].spacing;

        for (var k = 0; k < 2; ++k) {
          points[k][i] = axes[i].range[1] - frac;
          var l = (i + k + 1) % 3;
          var ratio = (axes[i].range[1] - axes[i].range[0]) / (axes[l].range[1] - axes[l].range[0]);
          points[k][l] = axes[l].range[0] + frac / ratio;
        }

        var points2d = points.map(function (val) {
          return _this.cartToxy(val);
        });
        this.gridGroup.append('path').datum(points2d).attr('d', gridlineFn).attr('stroke', 'rgba(0, 0, 0, 0.2)');
      }
    } // Add minor grid lines

  };

  TernaryPlot.prototype.drawData = function () {
    var _this = this;

    var axes = [this.dp.getAxis(0), this.dp.getAxis(1), this.dp.getAxis(2), this.dp.getAxis(3)];

    if (!axes[0]) {
      return;
    }

    var points3d = this.dp.samples.map(function (sample) {
      return [sample.composition[axes[0].element] || 0, sample.composition[axes[1].element] || 0, sample.composition[axes[2].element] || 0];
    });
    var hexFn = line().x(function (d) {
      return d[0];
    }).y(function (d) {
      return d[1];
    }).curve(curveLinearClosed);

    var fillFn = function fillFn(d, i) {
      if (!_this.colorMap) {
        return 'rgba(0, 0, 0, 0.2';
      }

      d;
      var color = scalarToColor(DataProvider.getSampleScalar(_this.dp.samples[i], _this.dp.getActiveScalar()), _this.colorMap, _this.colorMapRange);
      return "rgb(" + color[0] * 255 + ", " + color[1] * 255 + ", " + color[2] * 255 + ")";
    };

    var onMouseOver = function onMouseOver(d, i) {
      if (_this.mouseDown) {
        if (DataProvider.isSelected(_this.dp.samples[i], _this.selectedSamples)) {
          if (_this.onDeselect) {
            _this.onDeselect(_this.dp.samples[i]);
          }
        } else {
          if (_this.onSelect) {
            _this.onSelect(_this.dp.samples[i]);
          }
        }
      } // d is the datum, i is the index in the data


      var _a = d[0],
          x = _a[0],
          y = _a[1];

      _this.dataTooltip.html("\n        " + _this.dp.getAxisLabel(0) + ": " + points3d[i][0].toFixed(2) + "<br>\n        " + _this.dp.getAxisLabel(1) + ": " + points3d[i][1].toFixed(2) + "<br>\n        " + _this.dp.getAxisLabel(2) + ": " + points3d[i][2].toFixed(2) + "<br>\n        " + _this.dp.getAxisLabel(3) + ": " + (axes[3] ? (_this.dp.samples[i].composition[axes[3].element] || 0).toFixed(2) : '') + "<br>\n        " + _this.dp.getActiveScalar().replace("\\u002", '.') + ": " + DataProvider.getSampleScalar(_this.dp.samples[i], _this.dp.getActiveScalar()).toFixed(2) + "<br>\n      ");

      _this.dataTooltip.style('opacity', 0.9).style('left', x + 10 + "px").style('top', y - 10 + "px").style('transform', "translateY(-100%)");
    };

    var onMouseOut = function onMouseOut() {
      _this.dataTooltip.style('opacity', 0);
    };

    var onMouseDown = function onMouseDown(d, i) {
      d;

      if (DataProvider.isSelected(_this.dp.samples[i], _this.selectedSamples)) {
        if (_this.onDeselect) {
          _this.onDeselect(_this.dp.samples[i]);
        }
      } else {
        if (_this.onSelect) {
          _this.onSelect(_this.dp.samples[i]);
        }
      }
    };

    var hexagons = this.dataGroup.selectAll('path').data(points3d); // Enter

    hexagons.enter().append('path').datum(function (d) {
      return _this._makeHexagon(d);
    }).attr('d', hexFn).attr('fill', fillFn).attr('stroke', 'red').attr('stroke-width', function (d, i) {
      d;
      return DataProvider.isSelected(_this.dp.samples[i], _this.selectedSamples) ? 2 : 0;
    }).on('mouseover', onMouseOver).on('mouseout', onMouseOut).on('mousedown', onMouseDown); // Update

    hexagons.datum(function (d) {
      return _this._makeHexagon(d);
    }).attr('d', hexFn).attr('fill', fillFn).attr('stroke-width', function (d, i) {
      d;
      return DataProvider.isSelected(_this.dp.samples[i], _this.selectedSamples) ? 2 : 0;
    }).on('mouseover', onMouseOver).on('mouseout', onMouseOut).on('mousedown', onMouseDown); //Remove
  };

  TernaryPlot.prototype.setColorMap = function (map, range) {
    this.colorMap = map;
    this.colorMapRange = range;
    this.drawData();
  };

  TernaryPlot.prototype.setSelectedSamples = function (selectedSamples) {
    this.selectedSamples = selectedSamples;
  };

  TernaryPlot.prototype._makeHexagon = function (cart) {
    var axis = this.dp.getAxis(0);
    var delta = axis.range[1] - axis.range[0];
    var radius = 1 / Math.sqrt(3) * this.edge * axis.spacing / delta;
    radius *= 0.99;
    var center = this.cartToxy(cart);
    var points = [];

    for (var i = 0; i < 6; ++i) {
      var xy = [radius * Math.cos(Math.PI * (2 * i + 1) / 6) + center[0], radius * Math.sin(Math.PI * (2 * i + 1) / 6) + center[1]];
      points.push(xy);
    }

    return points;
  };

  TernaryPlot.prototype.dataUpdated = function () {
    this.scales = [];
    var axes = [this.dp.getAxis(0), this.dp.getAxis(1), this.dp.getAxis(2)];

    for (var i = 0; i < 3; ++i) {
      var range = axes[i] ? axes[i].range : [0, 1];
      this.scales.push(scaleLinear().domain(range).range([0, 1]));
    }
  };

  TernaryPlot.prototype.cartToxy = function (cart) {
    var xy = [0, 0];
    var frac = [0, 0, 0];

    for (var i = 0; i < 3; ++i) {
      frac[i] = this.scales[i](cart[i]);
    }

    xy[0] = 0.5 * ((2 * frac[1] + frac[2]) / (frac[0] + frac[1] + frac[2]));
    xy[1] = 0.5 * Math.sqrt(3) * frac[2] / (frac[0] + frac[1] + frac[2]);
    xy[0] *= this.edge;
    xy[1] *= this.edge;
    xy[0] += this.vertices[0][0];

    if (this.upsideDown) {
      xy[1] += this.vertices[0][1];
    } else {
      xy[1] = -xy[1] + this.vertices[0][1];
    }

    return xy;
  };

  return TernaryPlot;
}();

export { TernaryPlot };